/**
 * A payout plan, edited: how commission is released (on a schedule, on
 * request, or both), when, the least a payout may be, and the settlement
 * period. Used for the programme's default and for one partner's own plan.
 *
 * The words under the fields are the server's own plan description once
 * saved; what a date works out to is the server's to say (the next payout
 * date on the partner's page and the run list), never computed here.
 */

import { Input, Select } from '@connect/web-shared';

export interface PayoutPlan {
  release: 'scheduled' | 'on_request' | 'scheduled_and_request';
  cadence: 'monthly' | 'quarterly' | 'fortnightly' | 'weekly';
  anchor: 'nth_bank_day' | 'day_of_month' | 'last_bank_day';
  bank_day: number;
  day_of_month: number;
  weekday: number;
  minimum_payout: string;
  settlement_days: number;
}

export const DEFAULT_PLAN: PayoutPlan = {
  release: 'scheduled_and_request',
  cadence: 'monthly',
  anchor: 'nth_bank_day',
  bank_day: 2,
  day_of_month: 5,
  weekday: 0,
  minimum_payout: '1500.00',
  settlement_days: 30,
};

export function planFrom(value: Partial<PayoutPlan> | null | undefined): PayoutPlan {
  return { ...DEFAULT_PLAN, ...(value ?? {}) };
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

export function PlanEditor({ value, onChange }: { value: PayoutPlan; onChange: (plan: PayoutPlan) => void }) {
  const set = <K extends keyof PayoutPlan>(key: K, v: PayoutPlan[K]) => onChange({ ...value, [key]: v });
  const scheduled = value.release !== 'on_request';
  const weekly = value.cadence === 'weekly' || value.cadence === 'fortnightly';
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <Select
        label="Released"
        value={value.release}
        onChange={(e) => set('release', e.target.value as PayoutPlan['release'])}
        options={[
          { value: 'scheduled_and_request', label: 'On a schedule, and when asked' },
          { value: 'scheduled', label: 'On a schedule only' },
          { value: 'on_request', label: 'Only when the partner asks' },
        ]}
      />
      {scheduled ? (
        <>
          <Select
            label="How often"
            value={value.cadence}
            onChange={(e) => set('cadence', e.target.value as PayoutPlan['cadence'])}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'quarterly', label: 'Quarterly (from April)' },
              { value: 'fortnightly', label: 'Every other week' },
              { value: 'weekly', label: 'Weekly' },
            ]}
          />
          {weekly ? (
            <Select
              label="On"
              value={String(value.weekday)}
              onChange={(e) => set('weekday', Number(e.target.value))}
              options={WEEKDAYS.map((label, i) => ({ value: String(i), label }))}
              hint="Moved to the next bank working day if the bank is closed"
            />
          ) : (
            <Select
              label="On"
              value={value.anchor}
              onChange={(e) => set('anchor', e.target.value as PayoutPlan['anchor'])}
              options={[
                { value: 'nth_bank_day', label: 'A bank working day of the month' },
                { value: 'day_of_month', label: 'A date of the month' },
                { value: 'last_bank_day', label: 'The last bank working day' },
              ]}
            />
          )}
          {!weekly && value.anchor === 'nth_bank_day' ? (
            <Select
              label="Which bank working day"
              value={String(value.bank_day)}
              onChange={(e) => set('bank_day', Number(e.target.value))}
              options={Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: `${ordinal(i + 1)} bank working day` }))}
            />
          ) : null}
          {!weekly && value.anchor === 'day_of_month' ? (
            <Input
              label="Date"
              inputMode="numeric"
              value={String(value.day_of_month)}
              onChange={(e) => set('day_of_month', Number(e.target.value.replace(/\D/g, '') || '1'))}
              hint="1 to 28; moved to the next bank working day if closed"
            />
          ) : null}
        </>
      ) : null}
      <Input
        label="Least a payout may be ₹"
        inputMode="decimal"
        value={value.minimum_payout}
        onChange={(e) => set('minimum_payout', e.target.value.replace(/[^0-9.]/g, ''))}
        hint="Less carries over to the next payout"
      />
      <Input
        label="Settlement period (days)"
        inputMode="numeric"
        value={String(value.settlement_days)}
        onChange={(e) => set('settlement_days', Number(e.target.value.replace(/\D/g, '') || '0'))}
        hint="Commission is paid once this old, so a refund can still take it back"
      />
    </div>
  );
}

function ordinal(n: number): string {
  const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}
